import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Loader2, Mail, MessageCircle, Plus, ReceiptText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PER_PAGE, Pager } from "@/components/Pager";
import { api, errorMessage, openPdf } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { formatDate, money } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { PortalLinkSheet } from "@/components/PortalLinkSheet";

const Kpi = ({ label, value, tone = "", testId }) => (
  <div className="surface p-5" data-testid={testId}>
    <p className="label-caps">{label}</p>
    <p className={`num mt-3 text-xl text-slate-900 ${tone}`}>{value}</p>
  </div>
);

export default function ClientLedger() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/clients/${clientId}/ledger`);
      setData(res.data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load this client's ledger"));
      navigate("/clients", { replace: true });
    }
  }, [clientId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (error) {
      toast.error(errorMessage(error, "Could not send the statement"));
    } finally {
      setBusy("");
    }
  };

  const statementPayload = { scope: "client", target_id: clientId, date_from: null, date_to: null };

  const emailStatement = () =>
    act("email", async () => {
      const { data: res } = await api.post("/statements/send-email", statementPayload);
      toast.success(`Statement of account emailed to ${res.recipient}`);
    });

  const whatsappStatement = () =>
    act("whatsapp", async () => {
      const { data: res } = await api.post("/statements/whatsapp", statementPayload);
      openWhatsApp(res);
    });

  const removeBlock = (block) =>
    act(`block-${block.block_id}`, async () => {
      await api.delete(`/clients/${clientId}/billing-blocks/${block.block_id}`);
      toast.success("Saved billed-to block removed");
      await load();
    });

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="client-ledger-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const { client, totals, matters, contacts, buckets, bucket_labels: labels, payments } = data;
  const symbol = data.currency_symbol || "Rs.";
  const openBuckets = Object.entries(buckets || {}).filter(([, amount]) => Number(amount) > 0);

  return (
    <div className="space-y-8" data-testid="client-ledger-page">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <Link
            to="/clients"
            data-testid="back-to-clients"
            className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
            All clients
          </Link>
          <h1 className="mt-4 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
            {client.name}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {[client.city, client.email, client.phone].filter(Boolean).join(" · ") ||
              "No contact details on record"}
          </p>
          {client.tax_id ? (
            <p className="num mt-1 text-[12px] text-slate-500">GST / Tax No. {client.tax_id}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => openPdf(`/statements/pdf?scope=client&target_id=${clientId}`)}
            data-testid="client-statement-pdf-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
            Statement PDF
          </button>
          <button
            type="button"
            onClick={emailStatement}
            disabled={busy === "email"}
            data-testid="client-statement-email-button"
            className="inline-flex items-center gap-2 bg-slate-900 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
          >
            {busy === "email" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
            Email statement
          </button>
          <button
            type="button"
            onClick={whatsappStatement}
            disabled={busy === "whatsapp"}
            data-testid="client-statement-whatsapp-button"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:opacity-60"
          >
            {busy === "whatsapp" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
            WhatsApp
          </button>
          <PortalLinkSheet scope="client" id={clientId} name={client.name} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Billed" value={money(totals.billed, symbol)} testId="ledger-kpi-billed" />
        <Kpi
          label="Received"
          value={money(totals.received, symbol)}
          tone="text-emerald-700"
          testId="ledger-kpi-received"
        />
        <Kpi
          label="Outstanding"
          value={money(totals.outstanding, symbol)}
          testId="ledger-kpi-outstanding"
        />
        <Kpi
          label="Of which overdue"
          value={money(totals.overdue, symbol)}
          tone={Number(totals.overdue) > 0 ? "text-red-600" : ""}
          testId="ledger-kpi-overdue"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2" data-testid="ledger-matters">
          {matters.length === 0 ? (
            <div className="surface p-10 text-center" data-testid="ledger-no-matters">
              <ReceiptText className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
              <p className="mt-4 text-sm text-slate-600">
                No matters are linked to this client yet.
              </p>
            </div>
          ) : (
            matters.slice((page - 1) * PER_PAGE, page * PER_PAGE).map((matter) => (
              <section
                key={matter.case_id}
                className="surface min-w-0 p-5 sm:p-6"
                data-testid={`ledger-matter-${matter.case_id}`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="num text-[11px] uppercase tracking-[0.14em] text-slate-400">
                      {matter.matter_ref || "No reference"}
                    </p>
                    <h2 className="mt-2 font-serif text-lg leading-snug text-slate-900">
                      {matter.title}
                    </h2>
                    <p className="mt-1 text-[12px] text-slate-500">
                      {[matter.forum || "Forum not set", matter.billing_contact]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <Link
                    to={`/invoices/new?case=${matter.case_id}`}
                    data-testid={`ledger-bill-${matter.case_id}`}
                    className="inline-flex shrink-0 items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-700 transition-colors duration-150 hover:bg-slate-50"
                  >
                    <Plus className="h-3 w-3" strokeWidth={2.2} />
                    Fee note
                  </Link>
                </div>

                <dl className="mt-5 grid grid-cols-3 gap-4 border-y border-slate-100 py-4 text-[12px]">
                  <div>
                    <dt className="label-caps">Billed</dt>
                    <dd className="num mt-1 text-slate-900">{money(matter.billed, symbol)}</dd>
                  </div>
                  <div>
                    <dt className="label-caps">Received</dt>
                    <dd className="num mt-1 text-emerald-700">{money(matter.received, symbol)}</dd>
                  </div>
                  <div>
                    <dt className="label-caps">Outstanding</dt>
                    <dd className="num mt-1 text-slate-900">
                      {money(matter.outstanding, symbol)}
                    </dd>
                  </div>
                </dl>

                {matter.invoices.length === 0 ? (
                  <p className="mt-4 text-[12px] text-slate-500">No fee notes raised yet.</p>
                ) : (
                  <div className="mt-4 max-w-full overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="label-caps py-2.5 pr-4">Fee note</th>
                          <th className="label-caps py-2.5 pr-4">Raised</th>
                          <th className="label-caps py-2.5 pr-4">Due</th>
                          <th className="label-caps py-2.5 pr-4">Status</th>
                          <th className="label-caps py-2.5 pr-4 text-right">Total</th>
                          <th className="label-caps py-2.5 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {matter.invoices.map((row) => (
                          <tr
                            key={row.invoice_id}
                            onClick={() => navigate(`/invoices/${row.invoice_id}`)}
                            data-testid={`ledger-invoice-${row.invoice_number}`}
                            className="cursor-pointer transition-colors duration-150 hover:bg-slate-50"
                          >
                            <td className="num py-3 pr-4 text-[12px] text-slate-900">
                              {row.invoice_number}
                              {row.revision > 0 ? (
                                <span className="ml-1.5 text-[10px] uppercase tracking-wider text-slate-400">
                                  rev {row.revision}
                                </span>
                              ) : null}
                            </td>
                            <td className="num py-3 pr-4 text-[12px] text-slate-600">
                              {formatDate(row.issue_date)}
                            </td>
                            <td className="num py-3 pr-4 text-[12px] text-slate-600">
                              {formatDate(row.due_date)}
                            </td>
                            <td className="py-3 pr-4">
                              <StatusBadge status={row.display_status} />
                            </td>
                            <td className="num py-3 pr-4 text-right text-[12px] text-slate-900">
                              {money(row.total, symbol)}
                            </td>
                            <td className="num py-3 text-right text-[12px] text-slate-900">
                              {money(row.balance, symbol)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))
          )}
          <Pager
            page={page}
            total={matters.length}
            onPage={setPage}
            testId="client-matters-pager"
          />
        </div>

        <aside className="min-w-0 space-y-4">
          <div className="surface p-6" data-testid="ledger-aging">
            <h2 className="font-serif text-xl text-slate-900">Ageing</h2>
            {openBuckets.length === 0 ? (
              <p className="mt-4 text-[12px] text-slate-500">Nothing outstanding.</p>
            ) : (
              <dl className="mt-5 space-y-2.5 text-[13px]">
                {openBuckets.map(([key, amount]) => (
                  <div key={key} className="flex justify-between text-slate-600">
                    <dt>{labels?.[key] || key}</dt>
                    <dd className="num text-slate-900">{money(amount, symbol)}</dd>
                  </div>
                ))}
              </dl>
            )}
            <p className="mt-5 border-t border-slate-100 pt-4 text-[11px] text-slate-500">
              {totals.matter_count} matter(s) · {totals.invoice_count} fee note(s)
              {Number(totals.draft) > 0 ? ` · ${money(totals.draft, symbol)} still in draft` : ""}
            </p>
          </div>

          <div className="surface p-6" data-testid="ledger-contacts">
            <h2 className="font-serif text-xl text-slate-900">Contact persons</h2>
            {contacts.length === 0 ? (
              <p className="mt-4 text-[12px] text-slate-500">
                No contact person is attached to this client yet.
              </p>
            ) : (
              <ul className="mt-5 divide-y divide-slate-100">
                {contacts.map((person) => (
                  <li key={person.contact_id} className="py-3">
                    <p className="text-[13px] text-slate-900">{person.full_name}</p>
                    <p className="text-[11px] text-slate-400">
                      {[person.designation, person.email].filter(Boolean).join(" · ") ||
                        "No details"}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="surface p-6" data-testid="ledger-billing-blocks">
            <h2 className="font-serif text-xl text-slate-900">Saved billed-to blocks</h2>
            <p className="mt-1.5 text-[12px] text-slate-500">
              One-off billing details you chose to remember. They are offered on every new fee note
              for this client.
            </p>
            {(data.billing_blocks || []).length === 0 ? (
              <p className="mt-4 text-[12px] text-slate-500">
                None saved yet — tick &ldquo;remember this&rdquo; while raising a fee note.
              </p>
            ) : (
              <ul className="mt-5 divide-y divide-slate-100">
                {(data.billing_blocks || []).map((block) => (
                  <li
                    key={block.block_id}
                    className="flex items-start justify-between gap-3 py-3"
                    data-testid={`billing-block-${block.block_id}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[13px] text-slate-900">{block.name}</p>
                      <p className="truncate text-[11px] text-slate-400">
                        {[block.designation, block.org, block.city].filter(Boolean).join(" · ") ||
                          "No further details"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBlock(block)}
                      data-testid={`delete-billing-block-${block.block_id}`}
                      className="flex h-7 w-7 shrink-0 items-center justify-center border border-slate-200 text-slate-500 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {payments.length > 0 && (
            <div className="surface p-6" data-testid="ledger-receipts">
              <h2 className="font-serif text-xl text-slate-900">Latest receipts</h2>
              <ul className="mt-5 divide-y divide-slate-100">
                {[...payments].reverse().map((p) => (
                  <li key={p.payment_id} className="flex items-baseline justify-between py-2.5">
                    <div>
                      <p className="num text-[13px] text-slate-900">{money(p.amount, symbol)}</p>
                      <p className="text-[11px] text-slate-400">
                        {formatDate(p.paid_on)}
                        {p.receipt_no ? ` · ${p.receipt_no}` : ""}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openPdf(`/payments/${p.payment_id}/receipt.pdf`)}
                      data-testid={`ledger-receipt-${p.payment_id}`}
                      className="border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600 transition-colors duration-150 hover:border-slate-900 hover:text-slate-900"
                    >
                      Receipt
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
